import { HttpClient } from '@angular/common/http';
import { computed, Injectable, Injector, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import moment from 'moment-timezone';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, finalize, map, scan, switchMap } from 'rxjs/operators';

import { TradingLogSymbolSummaryPanelItemValuesModel } from '@c/trading-log/shared';
import { EasternTimeZoneName } from '@const';
import { Response } from '@core1/interface';
import { WheelScannerResponse, WheelSmiley } from '@core1/model';
import { getSellCallsMinOptionPremium, getSellCallsOptionPremiumAnnularized } from '@m1/wheel/wheel-calculator/formula';
import { WheelService } from '@m1/wheel/wheel.service';
import { RelativelyToMarketTimeOptions } from '@mod/admin';
import { ISymbolSmiley } from '@mod/symbol-smiley/symbol-smiley.model';
import { ObservableService as ObservableServiceV2 } from '@s/observable.service';
import {
  DividendsCount,
  EarningBeforeExpOptions,
  ExpirationType,
  IScannerResponse,
  IScannerResponseV2,
  IWheelFilter,
  IWheelStockOption,
  MinStrikeToLowestClose,
  SectorsFilter,
  WheelSellCallOptionExtendedModel,
  WheelSellCallOptionModel,
} from '@t/wheel/wheel.types';
import { convertToEasternTime, round } from '@u/utils';

@Injectable({ providedIn: 'root' })
export class WheelScannerService {
  public _symbolChart$: BehaviorSubject<Array<IWheelStockOption> | null> = new BehaviorSubject(null);

  public data$: BehaviorSubject<WheelScannerResponse[]> = new BehaviorSubject([]);

  // specific flag for wheel-data-window, to restore previous selected symbol (if it was selected from scanner)
  public firstScannerResponseReceived = new Subject<boolean>();

  public lastReceivedScannerData$: BehaviorSubject<Array<IWheelStockOption>> = new BehaviorSubject([]);
  public lastReceivedFilteredPortfolioSectorsList$ = new BehaviorSubject<string[]>([]);
  public uniqueListOfExpiration$ = new BehaviorSubject<string[]>([]);

  public lastUpdatedTime: BehaviorSubject<string> = new BehaviorSubject(null);

  public timer: BehaviorSubject<string> = new BehaviorSubject(null);

  public smileyTrigger$: BehaviorSubject<ISymbolSmiley[]> = new BehaviorSubject([]); // legacy, for old wheel-watchlist

  public onSelectChart$: BehaviorSubject<boolean> = new BehaviorSubject(null);

  // START: shared sell-call-options state: for min-strike-price on chart and table in data-window
  public readonly currentSummaryPanelValue$ = new BehaviorSubject<TradingLogSymbolSummaryPanelItemValuesModel | null>(
    null,
  );

  public readonly wheelScannerFilters = toSignal(this.observableServiceV2.wheelFiltersState, {
    injector: this.injector,
  });

  protected readonly tradingLogSymbolSummaryPanelWheelState = toSignal(
    this.observableServiceV2.tradingLogSymbolSummaryPanelWheelState,
    { injector: this.injector },
  );

  protected readonly sellCallOptions$ = combineLatest([
    this.observableServiceV2.wheelSymbol,
    this.wheelService.updateStockList$,
  ]).pipe(
    debounceTime(300),
    switchMap(([securityId]) =>
      this.getSellCallOptions(securityId).pipe(map((options) => ({ options, symbolId: securityId }))),
    ),
  );

  // Combined stream that ensures options and summary are always for the same symbol
  protected readonly sellCallOptionsData$ = combineLatest([
    this.sellCallOptions$,
    this.currentSummaryPanelValue$,
    this.observableServiceV2.wheelSymbol,
  ]).pipe(
    scan(
      (previousState, [optionsData, summaryValue, wheelSymbolId]) => {
        const optionsSymbolId = optionsData.symbolId;
        const isConsistent = optionsSymbolId === wheelSymbolId;

        if (isConsistent) {
          return {
            options: optionsData.options,
            summaryValue,
            symbolId: optionsSymbolId,
          };
        } else {
          return previousState;
        }
      },
      { options: [] as WheelSellCallOptionModel[], summaryValue: null, symbolId: null } as {
        options: WheelSellCallOptionModel[];
        summaryValue: TradingLogSymbolSummaryPanelItemValuesModel | null;
        symbolId: number | null;
      },
    ),
  );

  protected readonly sellCallOptionsData = toSignal(this.sellCallOptionsData$, { injector: this.injector });

  public readonly lastTradePrice = signal<{ security_id: number; price: number } | null>(null);

  public readonly sellCallOptionsExtended = computed(() => {
    // Get consistent data (options and summary guaranteed to be for the same symbol)
    const data = this.sellCallOptionsData();
    const options = data?.options ?? [];
    const currentSummaryPanelValue = data?.summaryValue;
    const wheelFilters = this.wheelScannerFilters();

    const stockPrice = currentSummaryPanelValue?.realValues?.costBasis || (options[0] ? options[0].price : 0);
    const priceMode = currentSummaryPanelValue?.realValues?.costBasis ? 'tradingLog' : 'lastTradePrice';

    const extendedOptions = options.map((option) => {
      const gainPercent = (option.strike_price - stockPrice) / stockPrice;
      const minOptionPremium = getSellCallsMinOptionPremium(stockPrice, option.expiration);
      const annularized = getSellCallsOptionPremiumAnnularized(
        stockPrice,
        option.option_premium,
        100,
        option.expiration,
      );

      return {
        ...option,
        stockPriceForCalculations: stockPrice,
        min: round(minOptionPremium, 2),
        annularized: round(annularized, 0),
        gainPercent: round(gainPercent * 100, 0),
      };
    });

    const filteredOptions = this.filterSellCallOptions(extendedOptions, wheelFilters as IWheelFilter, stockPrice);

    return { extendedOptions: filteredOptions, priceMode };
  });

  public readonly sellCallMinStrikePrice = computed(() => {
    const options = this.sellCallOptionsExtended().extendedOptions ?? [];

    if (!options || options.length === 0) {
      return null;
    }

    const validStrikeValues = [...options]
      .map((option) => {
        if (typeof option.strike_price !== 'number' || !isFinite(option.strike_price)) {
          return null;
        }

        return option.strike_price;
      })
      .filter((d) => d !== null);

    const minStrikeValue = validStrikeValues.length > 0 ? Math.max(...validStrikeValues) : null;

    if (!minStrikeValue) {
      return null;
    }

    let minStrikeLabel = 'High Call Strike: ';

    [...options]
      .filter((option) => option.strike_price === minStrikeValue)
      .sort((a, b) => moment(a.expiration).unix() - moment(b.expiration).unix())
      .forEach((option, index) => {
        minStrikeLabel += `${index === 0 ? '' : ' / '}${moment(option.expiration).format('MMM D')} - ${option.annularized}%`;
      });

    return { security_id: options[0].security_id, value: minStrikeValue, label: minStrikeLabel };
  });
  // END

  constructor(
    private http: HttpClient,
    private observableServiceV2: ObservableServiceV2,
    private injector: Injector,
    private wheelService: WheelService,
  ) {}

  // listened by data-window, passes stock-options
  public getSymbolChart$(): Observable<Array<IWheelStockOption> | null> {
    return this._symbolChart$.asObservable();
  }

  public setSymbolChart$(value: Array<IWheelStockOption>): void {
    this._symbolChart$.next(value);
  }

  public getWheelScannerDataV2(): Observable<IScannerResponseV2> {
    return this.http.post<{ result: IScannerResponseV2 }>('/v2/wheel/scannerV2', {}).pipe(
      map((res) => res.result),
      finalize(() => {}),
    );
  }

  public getSellCallOptions(symbolId: number | null): Observable<WheelSellCallOptionModel[]> {
    return this.http
      .post<Response<WheelSellCallOptionModel[]>>(`/v2/wheel/calls/${symbolId}`, {})
      .pipe(map((res) => res.result));
  }

  public getDataBySymbol(symbolId: number): Observable<IScannerResponse> {
    return this.http.post<{ result: IScannerResponse }>('/v2/wheel', { security_ids: [symbolId] }).pipe(
      map((res) => res.result),
      finalize(() => {}),
    );
  }

  public getWheelSmileyByUserId(): Observable<WheelSmiley[]> {
    return this.http.get<{ result: WheelSmiley[] }>('/wheel/smiley').pipe(map((res) => res.result));
  }

  private getExpirationFilterDate = (filter: ExpirationType, expirationDateOptions?: string[]): string | null => {
    const expirationDates = expirationDateOptions ?? this.uniqueListOfExpiration$.getValue();
    const expirationMap = {
      All: null,
      Nearest: expirationDates[0],
      Furthest: expirationDates[1],
    } as Record<ExpirationType, string | null>;
    return expirationMap[filter];
  };

  public filterStocks(
    data: Array<IWheelStockOption> | null,
    filters: IWheelFilter,
    expirationDateOptions: string[] = [],
    portfolioSectors: string[] = this.lastReceivedFilteredPortfolioSectorsList$.getValue(),
  ): Array<IWheelStockOption> | null {
    let filteredData = [...data];

    if (!filteredData || !filters) {
      return filteredData;
    }

    const expirationFilter = this.getExpirationFilterDate(filters.expiration, expirationDateOptions);

    const hasStrikeFromFilter = filters.strikeFrom !== '';
    const hasStrikeToFilter = filters.strikeTo !== '';
    const hasRoiFromFilter = filters.roiFrom !== '';
    const hasRoiToFilter = filters.roiTo !== '';
    const hasPremiumFromFilter = filters.premiumFrom !== '';
    const hasPremiumToFilter = filters.premiumTo !== '';
    const hasFlagsFilter = filters.flags.length > 0;
    const hasPERatioFromFilter = filters.peRatioFrom !== '';
    const hasPERatioToFilter = filters.peRatioTo !== '';
    const hasMarketCapFromFilter = filters.marketCapFrom.filterValue !== '';
    const hasMarketCapToFilter = filters.marketCapTo.filterValue !== '';
    const hasDividendsAtLeastOneFilter = filters.dividends === DividendsCount.AtLeastOne;
    const hasDividendsNoneFilter = filters.dividends === DividendsCount.None;
    const hasChangeInFromFilter = filters.changeInPercentFrom !== '';
    const hasChangeInToFilter = filters.changeInPercentTo !== '';
    const hasDropInFromFilter = filters.dropInPercentFrom !== '';
    const hasDropInToFilter = filters.dropInPercentTo !== '';

    filteredData = filteredData.filter(
      (el) =>
        (!hasStrikeFromFilter || el.strike_price >= (filters.strikeFrom as number)) &&
        (!hasStrikeToFilter || el.strike_price <= (filters.strikeTo as number)) &&
        (!hasRoiFromFilter || el.annularized >= (filters.roiFrom as number)) &&
        (!hasRoiToFilter || el.annularized <= (filters.roiTo as number)) &&
        (!hasPremiumFromFilter || el.option_premium >= (filters.premiumFrom as number)) &&
        (!hasPremiumToFilter || el.option_premium <= (filters.premiumTo as number)) &&
        (!expirationFilter || el.expiration === expirationFilter) &&
        (!hasFlagsFilter || filters.flags.includes(el.flag)) &&
        (!hasPERatioFromFilter || el.pe_ratio >= (filters.peRatioFrom as number)) &&
        (!hasPERatioToFilter || (el.pe_ratio <= (filters.peRatioTo as number) && el.pe_ratio !== null)) &&
        (!hasMarketCapFromFilter || el.market_cap >= (filters.marketCapFrom.filterValue as number)) &&
        (!hasMarketCapToFilter || el.market_cap <= (filters.marketCapTo.filterValue as number)) &&
        (!hasDividendsNoneFilter || el.dividends_count <= 0) &&
        (!hasDividendsAtLeastOneFilter || el.dividends_count > 0) &&
        (!hasChangeInFromFilter || el.price_change_percentage >= (filters.changeInPercentFrom as number)) &&
        (!hasChangeInToFilter || el.price_change_percentage <= (filters.changeInPercentTo as number)) &&
        (!hasDropInFromFilter ||
          (el.drop_percentage >= (filters.dropInPercentFrom as number) && el.drop_percentage !== null)) &&
        (!hasDropInToFilter ||
          (el.drop_percentage <= (filters.dropInPercentTo as number) && el.drop_percentage !== null)),
    );

    const selectedConsensusRatings = filters.consensusRatings ?? [];
    if (selectedConsensusRatings.length > 0) {
      filteredData = filteredData.filter(
        (el) => el.analyst_consensus_rating != null && selectedConsensusRatings.includes(el.analyst_consensus_rating),
      );
    }

    const hasPriceToAvgTargetFromFilter = filters.priceToAvgTargetFrom !== '' && filters.priceToAvgTargetFrom != null;
    const hasPriceToAvgTargetToFilter = filters.priceToAvgTargetTo !== '' && filters.priceToAvgTargetTo != null;

    if (hasPriceToAvgTargetFromFilter || hasPriceToAvgTargetToFilter) {
      filteredData = filteredData.filter((el) => {
        const percent = el.price_to_avg_target_percent;
        if (percent == null) {
          return false;
        }

        if (hasPriceToAvgTargetFromFilter && percent < Number(filters.priceToAvgTargetFrom)) {
          return false;
        }

        if (hasPriceToAvgTargetToFilter && percent > Number(filters.priceToAvgTargetTo)) {
          return false;
        }

        return true;
      });
    }

    if (filters.minStrike && filters.minStrike !== MinStrikeToLowestClose.AllStrikes) {
      const minStrikeDataBySymbol: { [key: string]: number } = {};

      filteredData.forEach((element) => {
        minStrikeDataBySymbol[element.symbol] =
          !minStrikeDataBySymbol[element.symbol] || minStrikeDataBySymbol[element.symbol] > element.strike_price
            ? element.strike_price
            : minStrikeDataBySymbol[element.symbol];
      });

      filteredData = filteredData.filter((el) => {
        switch (filters.minStrike) {
          case MinStrikeToLowestClose.Below:
            return minStrikeDataBySymbol[el.symbol] < el.close_40_min;
          case MinStrikeToLowestClose.AtLowestCloseInd:
            return (
              minStrikeDataBySymbol[el.symbol] <= el.close_40_min + 1 &&
              minStrikeDataBySymbol[el.symbol] >= el.close_40_min
            );
          case MinStrikeToLowestClose.AtOrBellowLowestInd:
            return minStrikeDataBySymbol[el.symbol] <= el.close_40_min + 1;
          default:
            return true;
        }
      });
    }

    if (filters.earningBeforeExp !== EarningBeforeExpOptions.Any) {
      filteredData = this.filterStockOptionsByEarningsBeforeExpiration(filteredData, filters, expirationDateOptions);
    }

    if (filters.sectors === SectorsFilter.ExcludePortfolioSectors) {
      filteredData = filteredData.filter((item) => !portfolioSectors.includes(item.sector));
    }

    return filteredData;
  }

  public filterStockOptions(
    data: Array<IWheelStockOption> | null,
    filters: IWheelFilter,
    expirationDateOptions: string[] = [],
  ): Array<IWheelStockOption> | null {
    let filteredData = [...data];

    if (!filteredData || !filters) {
      return [];
    }

    const hasStrikeFromFilter = filters.strikeFrom !== '';
    const hasStrikeToFilter = filters.strikeTo !== '';
    const hasRoiFromFilter = filters.roiFrom !== '';
    const hasRoiToFilter = filters.roiTo !== '';
    const hasPremiumFromFilter = filters.premiumFrom !== '';
    const hasPremiumToFilter = filters.premiumTo !== '';
    const hasDropInFromFilter = filters.dropInPercentFrom !== '';
    const hasDropInToFilter = filters.dropInPercentTo !== '';

    const expirationFilter = this.getExpirationFilterDate(filters.expiration, expirationDateOptions);

    filteredData = [...filteredData].filter(
      (el) =>
        (!hasStrikeFromFilter || Number(el.strike_price) >= Number(filters.strikeFrom)) &&
        (!hasStrikeToFilter || Number(el.strike_price) <= Number(filters.strikeTo)) &&
        (!hasRoiFromFilter || Number(el.annularized) >= Number(filters.roiFrom)) &&
        (!hasRoiToFilter || Number(el.annularized) <= Number(filters.roiTo)) &&
        (!hasPremiumFromFilter || Number(el.option_premium) >= Number(filters.premiumFrom)) &&
        (!hasPremiumToFilter || Number(el.option_premium) <= Number(filters.premiumTo)) &&
        (!expirationFilter || el.expiration === expirationFilter) &&
        (!hasDropInFromFilter ||
          (el.drop_percentage >= (filters.dropInPercentFrom as number) && el.drop_percentage !== null)) &&
        (!hasDropInToFilter ||
          (el.drop_percentage <= (filters.dropInPercentTo as number) && el.drop_percentage !== null)),
    );

    if (filters.earningBeforeExp !== EarningBeforeExpOptions.Any) {
      filteredData = this.filterStockOptionsByEarningsBeforeExpiration(filteredData, filters, expirationDateOptions);
    }

    return filteredData;
  }

  public filterSellCallOptions(
    options: WheelSellCallOptionExtendedModel[],
    filters: IWheelFilter,
    stockPurchasePrice: number,
  ): WheelSellCallOptionExtendedModel[] {
    if (!options || options.length === 0 || !filters) {
      return [];
    }

    // Hidden filters
    // Determine step based on purchased price
    let step: number;

    if (stockPurchasePrice < 100) {
      step = 0.5;
    } else if (stockPurchasePrice < 150) {
      step = 1;
    } else if (stockPurchasePrice < 3000) {
      step = 2.5;
    } else {
      step = 5;
    }

    const minStockPurchasePrice = stockPurchasePrice - step;
    const minRoi = 5; // 5%

    const filteredByHidden = [...options].filter(
      (el) => el.strike_price >= minStockPurchasePrice && el.annularized > minRoi, // ROI > 5%
    );

    // Filter by [expiration, premium, roi] based on selected filters
    const expirationFilter = this.getExpirationFilterDate(filters.expiration);
    const hasPremiumFromFilter = filters.premiumFrom !== '';
    const hasPremiumToFilter = filters.premiumTo !== '';
    const hasRoiFromFilter = filters.roiFrom !== '';
    const hasRoiToFilter = filters.roiTo !== '';

    return [...filteredByHidden].filter(
      (el) =>
        (!expirationFilter || el.expiration === expirationFilter) &&
        (!hasPremiumFromFilter || el.option_premium >= Number(filters.premiumFrom)) &&
        (!hasPremiumToFilter || el.option_premium <= Number(filters.premiumTo)) &&
        (!hasRoiFromFilter || el.annularized >= Number(filters.roiFrom)) &&
        (!hasRoiToFilter || el.annularized <= Number(filters.roiTo)),
    );
  }

  private filterStockOptionsByEarningsBeforeExpiration(
    data: Array<IWheelStockOption> | null,
    filters: IWheelFilter,
    expirationDateOptions: string[] = [],
  ): Array<IWheelStockOption> | null {
    if (!data) {
      return [];
    }

    if (!filters || filters.earningBeforeExp === EarningBeforeExpOptions.Any) {
      return [...data];
    }

    const currentDate = moment.tz(EasternTimeZoneName);

    // IMPORTANT: filter's logic is different from the text in selected option text or filter's hint
    // do not rely on "obvious" or straightforward logic, check Specs for details

    if (filters.earningBeforeExp === EarningBeforeExpOptions.NotBeforeExp) {
      return [...data].filter((item) => {
        // if no earnings for symbol - include it into the result
        if (!item.earning_report_date || !item.expiration) {
          return true;
        }

        const earningsDate = convertToEasternTime(item.earning_report_date);
        const isBeforeMarket = item.earning_before_after_market === RelativelyToMarketTimeOptions.BeforeMarket;
        const expirationDate = convertToEasternTime(item.expiration);

        if (earningsDate.isSame(currentDate, 'day') && isBeforeMarket) {
          return true;
        }

        // handle the case when [ED <= exp_1] AND [All OR exp_2] are selected in "Expiration" filter
        // should return items with [item.exp === exp_2]
        const exp1 = expirationDateOptions[0];
        const exp2 = expirationDateOptions[1];
        const exp1MomentET = exp1 ? convertToEasternTime(exp1) : null;
        const isEarningsSameOrBeforeExp1 = earningsDate.isSameOrBefore(exp1MomentET, 'day');

        if (exp1 && exp2 && isEarningsSameOrBeforeExp1) {
          return false;
        }

        // when [exp_1 < ED <= exp_2] OR [ED > exp_2] - use default behavior
        return earningsDate.isAfter(expirationDate, 'day');
      });
    }

    if (filters.earningBeforeExp === EarningBeforeExpOptions.OnlyBeforeExp) {
      return [...data].filter((item) => {
        const isBeforeMarket = item.earning_before_after_market === RelativelyToMarketTimeOptions.BeforeMarket;
        const earningsDate = item.earning_report_date ? convertToEasternTime(item.earning_report_date) : null;
        const expirationDate = item.expiration ? convertToEasternTime(item.expiration) : null;

        // if no earnings for symbol - include it into the result
        if (!earningsDate || !earningsDate.isValid() || !expirationDate || !expirationDate.isValid()) {
          return true;
        }

        // if earnings is today and before market - exclude it from the result
        if (earningsDate.isSame(currentDate, 'day') && isBeforeMarket) {
          return false;
        }

        // handle the case when [ED <= exp_1] AND [All OR exp_2] are selected in 'Expiration' filter
        // should return items with [item.exp === exp_2]
        const exp1 = expirationDateOptions[0];
        const exp2 = expirationDateOptions[1];

        const exp1MomentET = exp1 ? convertToEasternTime(exp1) : null;
        const exp2MomentET = exp2 ? convertToEasternTime(exp2) : null;

        const isEarningsSameBeforeExp1 = earningsDate.isSameOrBefore(exp1MomentET, 'day');
        const isEarningsAfterExp1 = earningsDate.isAfter(exp1MomentET, 'day');
        const isEarningsSameOrBeforeExp2 = earningsDate.isSameOrBefore(exp2MomentET, 'day');

        if (exp1MomentET?.isValid() && isEarningsSameBeforeExp1) {
          const expirationFilter = this.getExpirationFilterDate(filters.expiration, expirationDateOptions);
          const isAll = !expirationFilter;
          const isFirstExpiration = expirationFilter === exp1;
          const isSecondExpiration = expirationFilter === exp2;

          if (isAll) {
            return item.expiration === exp1 || item.expiration === exp2;
          } else if (isFirstExpiration) {
            return item.expiration === exp1;
          } else if (isSecondExpiration) {
            return item.expiration === exp2;
          }
        }

        if (exp1MomentET?.isValid() && exp2MomentET?.isValid() && isEarningsAfterExp1 && isEarningsSameOrBeforeExp2) {
          return item.expiration === exp2;
        }

        return false;
      });
    }

    return [...data];
  }
}
